import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Modal, Button, Input, Checkbox, Whisper, Popover, DatePicker } from 'rsuite';
import styled from 'styled-components';
import useSWR from 'swr';
import axios from 'axios';

import 'rsuite/Calendar/styles/index.css';
import 'rsuite/Modal/styles/index.css';
import 'rsuite/Button/styles/index.css';
import 'rsuite/Input/styles/index.css';
import 'rsuite/Checkbox/styles/index.css';
import 'rsuite/Popover/styles/index.css';
import 'rsuite/DatePicker/styles/index.css';

const DateCalendar = () => {
    const { data: datesData, mutate: refetchDates } = useSWR('/api/date', axios);
    const [nowDate, setNowDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingDate, setEditingDate] = useState(null);
    const [formData, setFormData] = useState({
        date: '',
        title: '',
        should_work: false,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const dates = useMemo(() => datesData?.data || [], [datesData]);

    function formatDate(date) {
        if (!date) return '';
        // Handle string format "YYYY-MM-DD"
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
            return date.split('T')[0]; // Remove time part if present
        }
        // Handle Date object
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return '';
        let year = d.getFullYear();
        let month = (d.getMonth() + 1).toString().padStart(2, '0');
        let day = d.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function formatDateForDisplay(date) {
        if (!date) return '';
        // Handle string format "YYYY-MM-DD"
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
            return date.split('T')[0];
        }
        // Handle Date object
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return '';
        let year = d.getFullYear();
        let month = (d.getMonth() + 1).toString().padStart(2, '0');
        let day = d.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function parseDateString(dateStr) {
        if (!dateStr) return null;
        const parts = dateStr.split('-');
        if (parts.length !== 3) return null;
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }

    function parseDateStringToDate(dateStr) {
        if (!dateStr) return null;
        // Handle string format "YYYY-MM-DD"
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const parts = dateStr.split('-');
            return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        }
        return dateStr instanceof Date ? dateStr : new Date(dateStr);
    }

    function parseDateStringToDate(dateStr) {
        if (!dateStr) return null;
        // Handle string format "YYYY-MM-DD"
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const parts = dateStr.split('-');
            return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        }
        return dateStr instanceof Date ? dateStr : new Date(dateStr);
    }

    const handleDateClick = useCallback((date) => {
        // 單擊：如果是已有日期，打開編輯彈窗
        const dateStr = formatDate(date);
        const existingDate = dates.find(d => {
            const dDateStr = formatDate(d.date);
            return dDateStr === dateStr;
        });
        
        if (existingDate) {
            setEditingDate(existingDate);
            setFormData({
                date: formatDate(existingDate.date),
                title: existingDate.title || '',
                should_work: existingDate.should_work || false,
            });
            setSelectedDate(date);
            setModalOpen(true);
            setError('');
        }
    }, [dates]);

    const handleDateDoubleClick = useCallback((date) => {
        // 雙擊：打開新增/編輯彈窗
        const dateStr = formatDate(date);
        const existingDate = dates.find(d => {
            const dDateStr = formatDate(d.date);
            return dDateStr === dateStr;
        });
        
        if (existingDate) {
            setEditingDate(existingDate);
            setFormData({
                date: formatDate(existingDate.date),
                title: existingDate.title || '',
                should_work: existingDate.should_work || false,
            });
        } else {
            setEditingDate(null);
            setFormData({
                date: dateStr,
                title: '',
                should_work: false,
            });
        }
        setSelectedDate(date);
        setModalOpen(true);
        setError('');
    }, [dates]);

    const handleNewButtonClick = useCallback(() => {
        // 新增按鈕：打開新增彈窗，預設為今天
        const today = new Date();
        const todayStr = formatDate(today);
        setEditingDate(null);
        setFormData({
            date: todayStr,
            title: '',
            should_work: false,
        });
        setSelectedDate(today);
        setModalOpen(true);
        setError('');
    }, []);

    const handleSave = async () => {
        setLoading(true);
        setError('');

        try {
            const payload = {
                date: formData.date,
                title: formData.title,
                should_work: formData.should_work,
            };

            let response;
            if (editingDate) {
                response = await axios.put(`/api/date/${editingDate.uuid}`, payload);
            } else {
                response = await axios.post('/api/date', payload);
            }

            await refetchDates();
            setModalOpen(false);
            setEditingDate(null);
            setFormData({ date: '', title: '', should_work: false });
        } catch (err) {
            let errorMessage = '操作失敗';
            if (err.response?.data) {
                const errorData = err.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (typeof errorData === 'object') {
                    const errors = Object.values(errorData).flat();
                    errorMessage = errors.join(', ');
                }
            }
            setError(errorMessage);
            if (window.swal) {
                window.swal('錯誤', errorMessage, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!editingDate) return;

        if (window.swal) {
            window.swal({
                title: "確定刪除?",
                text: "再確定一下喔!",
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "#DD6B55",
                confirmButtonText: "刪!",
                closeOnConfirm: true,
            }, async (confirmed) => {
                if (confirmed) {
                    setLoading(true);
                    try {
                        await axios.delete(`/api/date/${editingDate.uuid}`);
                        await refetchDates();
                        setModalOpen(false);
                        setEditingDate(null);
                        setFormData({ date: '', title: '', should_work: false });
                    } catch (err) {
                        if (window.swal) {
                            window.swal('錯誤', '刪除失敗', 'error');
                        }
                    } finally {
                        setLoading(false);
                    }
                }
            });
        }
    };

    const renderCell = useCallback((date) => {
        const dateStr = formatDate(date);
        const dateItem = dates.find(d => {
            const dDateStr = formatDate(d.date);
            return dDateStr === dateStr;
        });

        if (dateItem) {
            const isHoliday = !dateItem.should_work;
            return (
                <CellWrapper>
                    <DateIndicator 
                        isHoliday={isHoliday}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDateClick(date);
                        }}
                        onDoubleClick={(e) => {
                            e.stopPropagation();
                            handleDateDoubleClick(date);
                        }}
                    >
                        <DateTitle>{dateItem.title || (isHoliday ? '假日' : '補班')}</DateTitle>
                    </DateIndicator>
                </CellWrapper>
            );
        }

        return (
            <CellWrapper>
                <EmptyCell 
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleDateDoubleClick(date);
                    }}
                />
            </CellWrapper>
        );
    }, [dates, handleDateClick, handleDateDoubleClick]);

    return (
        <CalendarWrapper>
            <Header>
                <Title>例假日管理</Title>
                <Button 
                    appearance="primary" 
                    onClick={handleNewButtonClick}
                >
                    新增例假日
                </Button>
            </Header>
            <StyledCalendar 
                bordered 
                renderCell={renderCell} 
                weekStart={1} 
                value={nowDate} 
                onChange={setNowDate}
            />
            
            <Modal 
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setError('');
                }} 
                size="sm"
                backdrop={true}
                keyboard={true}
            >
                <Modal.Header>
                    <Modal.Title>{editingDate ? '編輯' : '新增'}例假日</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <ErrorText>{error}</ErrorText>}
                    <FormGroup>
                        <Label>日期</Label>
                        <DatePicker
                            value={parseDateStringToDate(formData.date)}
                            onChange={(value) => {
                                const dateStr = value ? formatDate(value) : '';
                                setFormData({...formData, date: dateStr});
                            }}
                            format="yyyy-MM-dd"
                            placeholder="選擇日期"
                            disabled={!!editingDate}
                            style={{ width: '100%' }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>名稱</Label>
                        <Input 
                            value={formData.title} 
                            onChange={(value) => setFormData({...formData, title: value})}
                            placeholder="例：春節、中秋節等"
                            autoFocus
                        />
                    </FormGroup>
                    <FormGroup>
                        <Checkbox 
                            checked={!formData.should_work}
                            onChange={(value, checked) => setFormData({...formData, should_work: !checked})}
                        >
                            是假日（不打勾則為補班日）
                        </Checkbox>
                    </FormGroup>
                </Modal.Body>
                <Modal.Footer>
                    {editingDate && (
                        <Button 
                            appearance="subtle" 
                            color="red" 
                            onClick={handleDelete}
                            disabled={loading}
                        >
                            刪除
                        </Button>
                    )}
                    <Button 
                        appearance="primary" 
                        onClick={handleSave}
                        loading={loading}
                    >
                        儲存
                    </Button>
                    <Button onClick={() => setModalOpen(false)} appearance="subtle">
                        取消
                    </Button>
                </Modal.Footer>
            </Modal>
        </CalendarWrapper>
    );
};

const CalendarWrapper = styled.div`
    background-color: white;
    border-radius: 8px;
    padding: 20px;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 24px;
`;

const StyledCalendar = styled(Calendar)`
    .rs-calendar-table-cell-content {
        position: relative;
    }
`;

const CellWrapper = styled.div`
    width: 100%;
    height: 100%;
    min-height: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
`;

const DateIndicator = styled.div`
    background-color: ${props => props.isHoliday ? '#ff6b6b' : '#4ecdc4'};
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    text-align: center;
    width: 100%;
    cursor: pointer;
    transition: opacity 0.2s;
    user-select: none;
    
    &:hover {
        opacity: 0.8;
    }
`;

const DateTitle = styled.div`
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const EmptyCell = styled.div`
    width: 100%;
    height: 100%;
    min-height: 20px;
    cursor: pointer;
    border: 1px dashed transparent;
    border-radius: 4px;
    transition: border-color 0.2s;
    user-select: none;
    
    &:hover {
        border-color: #ddd;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 15px;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
`;

const ErrorText = styled.div`
    color: #f44336;
    margin-bottom: 15px;
    padding: 10px;
    background-color: #ffebee;
    border-radius: 4px;
`;

export default DateCalendar;

